import { useCallback, useState } from 'react'
import { useTaskActualWorkApprovalRepository } from '../../../../services/adaptors/taskActualWorkApprovalStatusAdaptor'
import Auth from '../../../../lib/commons/auth'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { ProjectDetail } from '../../../../lib/functions/project'
import DateVO from '../../../../vo/DateVO'

export const useProjectClosingDialogs = (
  project: ProjectDetail,
  pageState: {
    initialized: boolean
  },
  closingStatus: {
    uuid: string | undefined
    closed: boolean
    closedAt: string | undefined
  },
  monthRange: { startDateString: string },
  fetchClosing: (projectUuid: string, startDateString: string) => Promise<any>,
  functionUuid: string
) => {
  const [showProjectClosingDialog, setShowProjectClosingDialog] =
    useState(false)

  const {
    closeProjectToTaskActualWorkApprovalStatus,
    cancelCloseProjectToTaskActualWorkApprovalStatus,
  } = useTaskActualWorkApprovalRepository()

  const onClickCloseProject = useCallback(async () => {
    if (!project?.uuid || !pageState.initialized) return
    const userUuid = Auth.getCurrentTenant()?.user!.uuid!
    const formatDate = new DateVO(monthRange.startDateString).formatForApi()
    const response = await closeProjectToTaskActualWorkApprovalStatus({
      uuid: closingStatus?.uuid,
      requestDate: formatDate!,
      userUuid: userUuid,
      projectUuid: project.uuid,
    })
    const fetchResponse = await fetchClosing(project.uuid, formatDate!)
    if (
      !response.hasError &&
      !response.hasWarning &&
      !fetchResponse.hasError &&
      !fetchResponse.hasWarning
    ) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({
            id: 'taskActualWorkApprovalStatus.projectClosingSuccess',
          }),
        })
      )
    }
  }, [
    project.uuid,
    pageState.initialized,
    closeProjectToTaskActualWorkApprovalStatus,
    closingStatus?.uuid,
    monthRange.startDateString,
    fetchClosing,
    functionUuid,
  ])

  const onClickCancelCloseProject = useCallback(async () => {
    if (!project?.uuid || !pageState.initialized) return
    const userUuid = Auth.getCurrentTenant()?.user!.uuid!
    const formatDate = new DateVO(monthRange.startDateString).formatForApi()
    const closeResponse =
      await cancelCloseProjectToTaskActualWorkApprovalStatus({
        uuid: closingStatus?.uuid,
        userUuid: userUuid,
        projectUuid: project.uuid,
        requestDate: formatDate!,
      })
    const response = await fetchClosing(project.uuid, formatDate!)
    if (
      !response.hasError &&
      !response.hasWarning &&
      !closeResponse.hasError &&
      !closeResponse.hasWarning
    ) {
      setShowProjectClosingDialog(false)
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({
            id: 'taskActualWorkApprovalStatus.projectClosingCancelSuccess',
          }),
        })
      )
    }
  }, [
    project?.uuid,
    pageState.initialized,
    closingStatus?.uuid,
    monthRange.startDateString,
    cancelCloseProjectToTaskActualWorkApprovalStatus,
    fetchClosing,
    functionUuid,
  ])

  const onClickProjectClosing = useCallback(() => {
    if (!project?.uuid || !pageState.initialized) return

    setShowProjectClosingDialog(true)
  }, [project?.uuid, pageState.initialized])

  const handleProjectClosingDialogSubmit = useCallback(() => {
    setShowProjectClosingDialog(false)
    if (closingStatus.closed) {
      onClickCancelCloseProject()
    } else {
      onClickCloseProject()
    }
  }, [onClickCloseProject, onClickCancelCloseProject, closingStatus.closed])

  const handleProjectClosingDialogClose = useCallback(() => {
    setShowProjectClosingDialog(false)
  }, [])

  return {
    showProjectClosingDialog,
    onClickProjectClosing,
    handleProjectClosingDialogSubmit,
    handleProjectClosingDialogClose,
  }
}
